import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, filter, finalize, of, switchMap, tap } from 'rxjs';
import { Construction } from 'src/app/models';
import { MouldDesignDataService } from 'src/app/services';
import { EquipmentLookupService } from 'src/app/state/equipment/equipment-lookup.service';

@Injectable({ providedIn: 'root' })
export class ConstructionDetailsService {
  private readonly _details = new BehaviorSubject<Construction | undefined>(undefined);
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _error = new BehaviorSubject<unknown>(undefined);

  public readonly details$ = this._details.asObservable();
  public readonly loading$ = this._loading.asObservable();
  public readonly error$ = this._error.asObservable();

  public constructor(
    private readonly mouldDesign: MouldDesignDataService,
    private readonly equipmentLookup: EquipmentLookupService
  ) {
    // Auto-load construction for selected equipmentId (for components that rely on it)
    this.equipmentLookup.selectedEquipmentId$
      .pipe(
        filter((id): id is string => !!id),
        switchMap((id) => this.loadConstruction(id))
      )
      .subscribe();
  }

  /** Allows manual loading (e.g. in MTS component) */
  public loadByEquipmentId(id: string): void {
    this.loadConstruction(id).subscribe();
  }

  private loadConstruction(equipmentNumber: string) {
    this._loading.next(true);
    this._error.next(undefined);

    return this.mouldDesign.getMouldCardByEquipmentNumber(equipmentNumber).pipe(
      tap((details) => {
        this._details.next(details);
      }),
      catchError((err) => {
        this._details.next(undefined);

        if (err instanceof HttpErrorResponse && err.status !== 404) {
          this._error.next(err);
        }

        return of(undefined);
      }),
      finalize(() => {
        this._loading.next(false);
      })
    );
  }

  public clear(): void {
    this._details.next(undefined);
    this._error.next(undefined);
  }
}
